import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AllRichContentSupportedTypes, RichContentSupportedTypes } from '../../../../../models';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../../models/Logging';
import { getContactFullName } from '../../../../../models/UiUtils';
import {
	AppSupportedTimelineTypes,
	ContactViewModel,
	RichContentViewModel,
	TimelineEventTypes,
	UserSessionContext,
} from '../../../../../viewmodels/AppViewModels';
import { THEME } from '../../../../components-v2/Theme/theme';
import {
	ContactAutomationsProgressBanner,
	IContactAutomationsProgressBannerComponent,
} from '../../../../components/contacts/ContactAutomationsProgressBanner';
import { ContactConnections } from '../../../../components/contacts/ContactConnections';
import { EntityDataBoards } from '../../../../components/dataBoards/EntityDataBoards';
import { ISelectOption, MultiSelect } from '../../../../components/DeprecatedSelect';
import { EntityAnalysis } from '../../../../components/entities/EntityAnalysis';
import { EntityRichContent } from '../../../../components/entities/EntityRichContent';
import { AnalysisIcon } from '../../../../components/svgs/icons/AnalysisIcon';
import { ConnectionsIcon } from '../../../../components/svgs/icons/ConnectionsIcon';
import { DataboardsIcon } from '../../../../components/svgs/icons/DataboardsIcon';
import { NotesIcon } from '../../../../components/svgs/icons/NotesIcon';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../../components/Tabs';
import { QuickActions } from './QuickActions';
import { styleSheet } from './styles';
import { ContactTimelineV2 } from './TimeLine';

const typesStorageKey = 'timelineFilterOptions';
const richContentStorageKey = 'richContentFilterOptions';
const expandedToggleStorageKey = 'timelineExpandedToggle';

export interface IContactActivityComponent {
	showAnalysisTab(): void;
	showRichContentTab(options?: { manageAutomationId?: string }): void;
}

interface IProps extends IEventLoggingComponentProps, RouteComponentProps<any> {
	className?: StyleDeclarationValue[];
	contact?: ContactViewModel;
	innerRef?(ref?: IContactActivityComponent): void;
	userSession?: UserSessionContext;
	setShouldStartPollingForRelationshipUpdate?(poll: boolean): void;
}

interface IState {
	timelineEventTypes?: TimelineEventTypes[];
	richContentTypes?: RichContentSupportedTypes[];
	expandAllTimeline: boolean;
	selectedTab: ContactActivityDataContext;
}

enum ContactActivityDataContext {
	Analysis = 'Analysis',
	Connections = 'Connections',
	History = 'History',
	RichContent = 'RichContent',
	DataBoards = 'Data Boards',
	Timeline = 'Timeline',
}

export interface ITimelineCriteria {
	name: string;
	type: TimelineEventTypes | 'All' | TimelineEventTypes[];
}

export interface IRichContentCriteria {
	name: string;
	type: RichContentSupportedTypes | 'All';
}

class _ContactActivity extends React.Component<IProps, IState> implements IContactActivityComponent {
	private mContactAutomationsProgressBannerComponentRef: IContactAutomationsProgressBannerComponent;
	private mTimelineInvalidateFunction: (() => void) | null = null;
	private static AllTimelineFilterOptions: ISelectOption<ITimelineCriteria>[];
	private static AllRichContentFilterOptions: ISelectOption<IRichContentCriteria>[];

	public constructor(props: IProps) {
		super(props);

		if (!_ContactActivity.AllTimelineFilterOptions) {
			_ContactActivity.AllTimelineFilterOptions = [
				{
					dataContext: {
						name: 'all',
						type: 'All',
					},
					forceSelectAll: true,
					id: 'rich-content-filter-option-show-all',
					text: 'Show all',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'notes',
						type: 'NoteEvent',
					},
					id: 'rich-content-filter-option-show-notes',
					text: 'Show notes',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'action items',
						type: 'ActionItemEvent',
					},
					id: 'rich-content-filter-option-show-action-items',
					text: 'Show action items',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'emails',
						type: ['SentEmailEvent', 'HtmlNewsletterEvent'],
					},
					id: 'rich-content-filter-option-show-emails',
					text: 'Show emails',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'conversations',
						type: 'ConversationThreadEvent',
					},
					id: 'rich-content-filter-option-show-texts',
					text: 'Show texts',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'activities outside Levitate',
						type: ['MeetingEvent', 'ExternalSentEmailEvent'],
					},
					id: 'rich-content-filter-option-show-activities-outside-levitate',
					text: 'Show activities outside Levitate',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'phone calls',
						type: 'PhoneCallEvent',
					},
					id: 'rich-content-filter-option-show-phone-calls',
					text: 'Show phone calls',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'surveys',
						type: 'SurveyResponseEvent',
					},
					id: 'rich-content-filter-option-show-surveys',
					text: 'Show surveys',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'handwritten cards',
						type: 'HandwrittenCardOrderEvent',
					},
					id: 'rich-content-filter-option-show-handwritten-cards',
					text: 'Show handwritten cards',
					type: 'checkbox',
				},
			];
		}
		if (!_ContactActivity.AllRichContentFilterOptions) {
			_ContactActivity.AllRichContentFilterOptions = [
				{
					dataContext: {
						name: 'all',
						type: 'All',
					},
					forceSelectAll: true,
					id: 'rich-content-filter-option-show-all',
					text: 'Show all',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'emails',
						type: 'EmailNote',
					},
					id: 'rich-content-filter-option-show-emails',
					text: 'Show emails',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'notes',
						type: 'Note',
					},
					id: 'rich-content-filter-option-show-notes',
					text: 'Show notes',
					type: 'checkbox',
				},
				{
					dataContext: {
						name: 'action items',
						type: 'ActionItem',
					},
					id: 'rich-content-filter-option-show-action-items',
					text: 'Show action items',
					type: 'checkbox',
				},
			];
		}
		this.state = {
			selectedTab: props.userSession.account.features.timeline?.enabled
				? ContactActivityDataContext.Timeline
				: ContactActivityDataContext.RichContent,
			expandAllTimeline: false,
		};
	}

	public componentDidMount() {
		const { innerRef } = this.props;
		if (innerRef) {
			innerRef(this);
		}
		this.getRichContentSupportedTypes();
		this.getTimelineSupportedTypes();
		this.getExpandedToggleState();
	}

	public componentWillUnmount() {
		const { innerRef } = this.props;
		if (innerRef) {
			innerRef(null);
		}
	}

	public render() {
		const { selectedTab, timelineEventTypes, richContentTypes, expandAllTimeline } = this.state;
		const { className, userSession, contact, setShouldStartPollingForRelationshipUpdate } = this.props;
		const canViewContactHistory =
			!!userSession.user.emailProviderFeatures.calendar.canViewHistoryByContact ||
			!!userSession.user.emailProviderFeatures.email.canViewHistoryByContact;
		const shouldShowTimeline = !!userSession.account.features.timeline?.enabled;
		const { policiesEnabled, donationsEnabled, opportunitiesEnabled } = userSession.account.features?.boards ?? {};
		const showAutomation = contact?.inProgressAutomations?.length > 0;
		return (
			<div className={css(styleSheet.contactActivity, className)}>
				<TabRoot
					className={css(styleSheet.tabRoot)}
					value={selectedTab}
					onValueChange={value => this.setState({ selectedTab: value as ContactActivityDataContext })}
				>
					<TabList styles={[styleSheet.tabs]}>
						{shouldShowTimeline ? (
							<TabTrigger variant='none' value={ContactActivityDataContext.Timeline} styles={[styleSheet.tabTrigger]}>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
									<NotesIcon />
								</span>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>History</span>
							</TabTrigger>
						) : null}
						{!shouldShowTimeline ? (
							<TabTrigger
								variant='none'
								value={ContactActivityDataContext.RichContent}
								styles={[styleSheet.tabTrigger]}
							>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
									<NotesIcon />
								</span>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>History</span>
							</TabTrigger>
						) : null}
						{!shouldShowTimeline && canViewContactHistory ? (
							<TabTrigger variant='none' value={ContactActivityDataContext.Analysis} styles={[styleSheet.tabTrigger]}>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
									<AnalysisIcon />
								</span>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Analysis</span>
							</TabTrigger>
						) : null}
						{userSession.userRole !== 'limitedUser' ? (
							<TabTrigger
								variant='none'
								value={ContactActivityDataContext.Connections}
								styles={[styleSheet.tabTrigger]}
							>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
									<ConnectionsIcon />
								</span>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Connections</span>
							</TabTrigger>
						) : null}
						{policiesEnabled || donationsEnabled || opportunitiesEnabled ? (
							<TabTrigger variant='none' value={ContactActivityDataContext.DataBoards} styles={[styleSheet.tabTrigger]}>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
									<DataboardsIcon fill={THEME.colors.text.light} />
								</span>
								<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Data Boards</span>
							</TabTrigger>
						) : null}
					</TabList>
					<TabContent value={ContactActivityDataContext.Timeline} asChild>
						<>
							<QuickActions
								contact={contact.toJs()}
								onContentCreated={() => {
									// Trigger timeline refresh when content is created
									if (this.mTimelineInvalidateFunction) {
										this.mTimelineInvalidateFunction();
									}
								}}
							/>
							{showAutomation && (
								<ContactAutomationsProgressBanner
									contact={contact}
									onRef={this.onContactAutomationsProgressBannerComponentRef}
								/>
							)}
							<div className={css(styleSheet.timelineFilterOptionsContainer)}>
								<label className={css(styleSheet.expandToggleLabel)}>
									<input
										type='checkbox'
										checked={expandAllTimeline}
										onChange={this.handleExpandToggle}
										className={css(styleSheet.expandToggleCheckbox)}
									/>
									Expand all
								</label>
								<MultiSelect
									onClose={this.onTimelineFilterClose}
									options={_ContactActivity.AllTimelineFilterOptions}
									dropdownStyles={[styleSheet.dropdown]}
									selectedOptions={this.getDefaultTimelineOptions()}
									selectAllIfNoneSelected={false}
									optionsStyle={{ maxHeight: 'auto' }}
									selectedOptionsTitle={this.getFilterOptionsTitle}
								/>
							</div>
							<ContactTimelineV2
								contactId={contact.id}
								contactName={getContactFullName(contact)}
								emailAddresses={contact.emailAddresses}
								onDeleteRichContent={(richContent: RichContentViewModel) => contact.deleteRichContent(richContent)}
								eventTypes={timelineEventTypes}
								expandAllTimeline={expandAllTimeline}
								setShouldStartPollingForRelationshipUpdate={setShouldStartPollingForRelationshipUpdate}
								onTimelineReady={invalidateTimeline => {
									// Store reference to the timeline's invalidate function
									this.mTimelineInvalidateFunction = invalidateTimeline;
								}}
							/>
						</>
					</TabContent>
					<TabContent value={ContactActivityDataContext.RichContent}>
						<div className={css(styleSheet.richContentFilterOptionsContainer)}>
							<MultiSelect
								onClose={this.onRichContentFilterClose}
								options={_ContactActivity.AllRichContentFilterOptions}
								dropdownStyles={[styleSheet.dropdown]}
								selectedOptions={this.getDefaultRichContentFilterOptions()}
								selectAllIfNoneSelected={true}
								selectedOptionsTitle={this.getFilterOptionsTitle}
							/>
						</div>
						{contact?.inProgressAutomations?.length > 0 && (
							<ContactAutomationsProgressBanner
								contact={contact}
								onRef={this.onContactAutomationsProgressBannerComponentRef}
							/>
						)}
						<EntityRichContent entity={contact} supportedTypes={richContentTypes} />
					</TabContent>
					<TabContent value={ContactActivityDataContext.Analysis}>
						<EntityAnalysis entity={contact} />
					</TabContent>
					<TabContent value={ContactActivityDataContext.Connections}>
						<ContactConnections contact={contact} />
					</TabContent>
					<TabContent value={ContactActivityDataContext.DataBoards}>
						<EntityDataBoards entityViewModel={contact} />
					</TabContent>
				</TabRoot>
			</div>
		);
	}

	public showAnalysisTab = () => {
		this.showTabWithDataContext(ContactActivityDataContext.Analysis);
	};

	public showRichContentTab = (options?: { manageAutomationId: string }) => {
		this.showTabWithDataContext(ContactActivityDataContext.RichContent);
		if (options?.manageAutomationId) {
			this.mContactAutomationsProgressBannerComponentRef?.showManageModal(options.manageAutomationId);
		}
	};

	private showTabWithDataContext = (dataContext: ContactActivityDataContext) => {
		this.setState({ selectedTab: dataContext });
	};

	private getRichContentSupportedTypes = () => {
		const storedTypes = window.localStorage.getItem(richContentStorageKey)?.split('|');
		const richContentTypes =
			!storedTypes || storedTypes.length === 0 || storedTypes.includes('All')
				? AllRichContentSupportedTypes
				: storedTypes.filter(option => option !== 'All');

		this.setState({
			richContentTypes: richContentTypes as RichContentSupportedTypes[],
		});
	};

	private getTimelineSupportedTypes = () => {
		const storedTypes = window.localStorage.getItem(typesStorageKey)?.split('|');
		const timelineTypes =
			!storedTypes || storedTypes.length === 0 || storedTypes.includes('All')
				? AppSupportedTimelineTypes
				: storedTypes.filter(option => option !== 'All');

		this.setState({
			timelineEventTypes: timelineTypes as TimelineEventTypes[],
		});
	};

	private getExpandedToggleState = () => {
		const storedState = window.localStorage.getItem(expandedToggleStorageKey);
		this.setState({
			expandAllTimeline: storedState === 'true',
		});
	};

	private handleExpandToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newState = e.target.checked;
		window.localStorage.setItem(expandedToggleStorageKey, newState.toString());
		this.setState({ expandAllTimeline: newState });
	};

	private getDefaultRichContentFilterOptions = (): ISelectOption<IRichContentCriteria>[] => {
		const storedOptions = window.localStorage.getItem(richContentStorageKey)?.split('|');
		return _ContactActivity.AllRichContentFilterOptions.filter(option => {
			if (!storedOptions || storedOptions.length === 0) {
				return option;
			}

			return storedOptions.includes(option.dataContext.type);
		});
	};

	private getDefaultTimelineOptions = (): ISelectOption<ITimelineCriteria>[] => {
		const storedOptionsStr = window.localStorage.getItem(typesStorageKey);
		if (!storedOptionsStr) {
			return _ContactActivity.AllTimelineFilterOptions;
		}
		const storedOptions = storedOptionsStr.split('|');
		if (storedOptions.includes('All')) {
			return _ContactActivity.AllTimelineFilterOptions;
		}
		return _ContactActivity.AllTimelineFilterOptions.filter(option => {
			if (Array.isArray(option.dataContext.type)) {
				return option.dataContext.type.some(x => storedOptions.includes(x));
			}

			return storedOptions.includes(option.dataContext.type);
		});
	};

	private getFilterOptionsTitle(selectedOptions: ISelectOption<ITimelineCriteria | IRichContentCriteria>[]) {
		if (selectedOptions.find(option => option.dataContext.type === 'All')) {
			return <div>Show all</div>;
		}

		const maxCharCount = 25;
		const text = `Show ${selectedOptions.map(option => option.dataContext.name).join(' and ')}`;
		return <div>{text.length > maxCharCount ? `${text.slice(0, maxCharCount)}...` : text}</div>;
	}

	private onTimelineFilterClose = (selectedOptions: ISelectOption<ITimelineCriteria>[]) => {
		let selected: string[] = [];
		selectedOptions.forEach(option => {
			const eventType = option.dataContext.type;
			if (Array.isArray(eventType)) {
				selected = selected.concat(eventType);
				return;
			}
			selected.push(eventType);
		});
		window.localStorage.setItem(typesStorageKey, selected.join('|'));
		this.getTimelineSupportedTypes();
	};

	private onContactAutomationsProgressBannerComponentRef = (ref?: IContactAutomationsProgressBannerComponent) => {
		this.mContactAutomationsProgressBannerComponentRef = ref;
	};

	private onRichContentFilterClose = (selectedOptions: ISelectOption<IRichContentCriteria>[]) => {
		const selected = selectedOptions.map(option => option.dataContext.type);
		window.localStorage.setItem(richContentStorageKey, selected.join('|'));
		this.getRichContentSupportedTypes();
	};
}

const ContactActivityAsObserver = observer(_ContactActivity);
const ContactActivityWithRouter = withRouter(ContactActivityAsObserver);
export const ContactActivity = withEventLogging(ContactActivityWithRouter, 'ContactActivity');
